import type { ContactInfoFieldValue } from "@jugl-web/domain-resources/contacts/utils";
import type { CRMChannel } from "@jugl-web/rest-api";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React from "react";
import { ReactComponent as InfoIcon } from "./assets/info.svg";

export const ContactIntelligenceFieldValue: React.FC<{
  value: ContactInfoFieldValue;
  onConversationNavigate?: (params: {
    channel: CRMChannel;
    convId: string;
  }) => void;
}> = ({ value, onConversationNavigate }) => {
  const { t } = useTranslations();

  if (value === null) {
    return (
      <span className="jugl__break-word font-secondary text-[13px] leading-[140%] font-normal text-dark-700">
        {t({
          id: "contacts-page.customer-info.no-info-found",
          defaultMessage: "No info found",
        })}
      </span>
    );
  }

  switch (value.type) {
    case "plain":
      return (
        <span className="jugl__break-word font-secondary text-[13px] leading-[140%] font-medium text-dark">
          {value.value}
        </span>
      );
    case "link": {
      const linkClassName =
        "text-primary-800 font-secondary jugl__break-word text-[13px] font-normal leading-[140%] tracking-[0.12px] underline";

      if (value.href) {
        return (
          <a
            href={value.href}
            target="_blank"
            rel="noreferrer"
            className={linkClassName}
          >
            {value.value}
          </a>
        );
      }

      return <span className={linkClassName}>{value.value}</span>;
    }
    case "conversation":
      if (!value.isChannelAccountConnected) {
        return (
          <div className="flex items-center gap-2">
            <span className="jugl__break-word font-secondary text-[13px] leading-[140%] font-medium tracking-[0.12px] text-dark-600 underline">
              {value.value}
            </span>
            <Tooltip
              placement="top"
              renderTrigger={({ props, ref }) => (
                <InfoIcon {...props} ref={ref} />
              )}
            >
              {t({
                id: "contacts-page.conversation-account-disconnected",
                defaultMessage:
                  "Account was disconnected. Reconnect to see the Conversation with Customer ℹ️",
              })}
            </Tooltip>
          </div>
        );
      }

      return (
        <PlainButton
          className="jugl__break-word font-secondary text-[13px] leading-[140%] font-normal tracking-[0.12px] text-primary-800 underline"
          onClick={() =>
            onConversationNavigate?.({
              channel: value.channel,
              convId: value.convId,
            })
          }
        >
          {value.value}
        </PlainButton>
      );
    case "notApplicable":
      return (
        <span className="jugl__break-word font-secondary text-[13px] leading-[140%] text-[#4F4F4F]">
          {t({
            id: "common.not-applicable-short",
            defaultMessage: "N/A",
          })}
        </span>
      );
    case "dash":
      return (
        <span className="jugl__break-word font-secondary text-[13px] leading-[140%] font-medium text-dark">
          -
        </span>
      );
    default:
      return null;
  }
};
